import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUndo, FaBookmark, FaBookmark as FaBookmarkSolid } from 'react-icons/fa';
import { jobPostingService, JobPosting, JobType } from '../services/jobPostingService';

interface JobPostingWithContent extends Omit<JobPosting, 'jobDescription'> {
  jobDescription?: string; // Added when fetching content from jobDescriptionId
}
import LoadingSpinner from '../components/LoadingSpinner';
import { useMobile } from '../hooks/useMobile';
import CareersHeader from '../components/CareersHeader';
import FiltersSidebar from '../components/FiltersSidebar';
import FloatingFilterButton from '../components/FloatingFilterButton';
import { apiClient } from '../services/apiClient';

const Careers: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [jobPostings, setJobPostings] = useState<JobPostingWithContent[]>([]);
  const [filteredPostings, setFilteredPostings] = useState<JobPostingWithContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobPostings, setLoadingJobPostings] = useState(true);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    jobTypes: [] as JobType[],
    positions: [] as string[],
  });

  // Applied filters (what's actually being used)
  const [appliedFilters, setAppliedFilters] = useState({
    jobTypes: [] as JobType[],
    positions: [] as string[],
  });

  // Load job postings
  useEffect(() => {
    loadJobPostings();
    // Load saved jobs from localStorage
    const saved = localStorage.getItem('savedJobs');
    if (saved) {
      setSavedJobs(new Set(JSON.parse(saved)));
    }
  }, []);

  // Apply filters when job postings or applied filters change
  useEffect(() => {
    applyFilters();
  }, [jobPostings, appliedFilters]);

  const loadJobPostings = async () => {
    try {
      setLoadingJobPostings(true);
      console.log('Loading job postings...');
      const postings = await jobPostingService.getAllJobPostings();
      console.log('Job postings loaded:', postings);
      
      // Set postings from API, or empty array if none
      if (!postings || postings.length === 0) {
        setJobPostings([]);
        setFilteredPostings([]);
      } else {
        // Fetch job description content for each posting that has a jobDescriptionId
        const postingsWithContent = await Promise.all(
          postings.map(async (posting: any) => {
            if (posting.jobDescriptionId) {
              try {
                const contentResponse = await apiClient.get(`/job-descriptions/${posting.jobDescriptionId}/content`);
                return { ...posting, jobDescription: contentResponse.data?.content || '' };
              } catch (error: any) {
                console.error(`Error loading content for job posting ${posting.id}:`, error);
                return { ...posting, jobDescription: '' };
              }
            }
            return { ...posting, jobDescription: '' };
          })
        );
        
        setJobPostings(postingsWithContent);
        setFilteredPostings(postingsWithContent);
      }
    } catch (error: any) {
      console.error('Error loading job postings:', error);
      // Don't show errors for empty responses (404, empty array) - just set empty arrays
      // Only log actual server errors (500+) for debugging, but don't show toasts
      if (error.response?.status && error.response.status >= 500) {
        console.error('Server error loading job postings:', error?.response?.data || error?.message);
      }
      // Set empty arrays to show "No data" message - don't show error toasts
      setJobPostings([]);
      setFilteredPostings([]);
    } finally {
      setLoadingJobPostings(false);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...jobPostings];

    // Filter by job type
    if (appliedFilters.jobTypes.length > 0) {
      filtered = filtered.filter(posting => posting.jobType && appliedFilters.jobTypes.includes(posting.jobType));
    }

    // Filter by position
    if (appliedFilters.positions.length > 0) {
      filtered = filtered.filter(posting => posting.position && appliedFilters.positions.includes(posting.position));
    }

    setFilteredPostings(filtered);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters({
      jobTypes: [],
      positions: [],
    });
    setAppliedFilters({
      jobTypes: [],
      positions: [],
    });
  };

  const handleJobTypeToggle = (jobType: JobType) => {
    setFilters(prev => ({
      ...prev,
      jobTypes: prev.jobTypes.includes(jobType)
        ? prev.jobTypes.filter(t => t !== jobType)
        : [...prev.jobTypes, jobType],
    }));
  };

  const handlePositionToggle = (position: string) => {
    setFilters(prev => ({
      ...prev,
      positions: prev.positions.includes(position)
        ? prev.positions.filter(p => p !== position)
        : [...prev.positions, position],
    }));
  };

  const handleSaveJob = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSavedJobs = new Set(savedJobs);
    if (newSavedJobs.has(jobId)) {
      newSavedJobs.delete(jobId);
    } else {
      newSavedJobs.add(jobId);
    }
    setSavedJobs(newSavedJobs);
    localStorage.setItem('savedJobs', JSON.stringify(Array.from(newSavedJobs)));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('savedJobsUpdated'));
  };

  const handleReadMore = (posting: JobPostingWithContent) => {
    navigate(`/job/${posting.id}`);
  };

  const extractJobSummary = (content: string, wordCount: number = 30): string => {
    if (!content) return '';
    
    let summaryText = '';
    
    // Find "Job Summary" (case insensitive) and extract text that follows it
    // Pattern: "Job Summary" (with optional colon/dash) followed by newline(s) and then capture text
    const summaryRegex = /Job\s+Summary\s*[:\-]?\s*\n+\s*(.+?)(?=\n\n|\n[A-Z][a-z]+\s*[:\-]|$)/is;
    const match = content.match(summaryRegex);
    
    if (match && match[1]) {
      summaryText = match[1].trim();
    } else {
      // Fallback: Try "Summary" alone
      const summaryRegex2 = /Summary\s*[:\-]?\s*\n+\s*(.+?)(?=\n\n|\n[A-Z][a-z]+\s*[:\-]|$)/is;
      const match2 = content.match(summaryRegex2);
      
      if (match2 && match2[1]) {
        summaryText = match2[1].trim();
      } else {
        // If no specific section found, use the entire content
        summaryText = content.trim();
      }
    }
    
    // Clean up the text: remove extra whitespace and normalize
    summaryText = summaryText.replace(/\s+/g, ' ').trim();
    
    // Split into words and take approximately 30 words
    const words = summaryText.split(/\s+/);
    
    if (words.length <= wordCount) {
      return summaryText;
    }
    
    // Take first 30 words and add ellipsis
    const truncatedWords = words.slice(0, wordCount);
    return truncatedWords.join(' ') + '...';
  };

  const POSITION_OPTIONS = ['Prepper', 'Cleaner', 'Detailer', 'Delivery Driver', 'Tunnel Driver'];

  // Debug: Log component render
  console.log('Careers component rendering', { loading, jobPostings: jobPostings.length, filteredPostings: filteredPostings.length });

  return (
    <div className="min-h-screen bg-gray-50">
      <CareersHeader />

      {/* Content with top padding to account for fixed header */}
      <div className="pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Sidebar - Filters (Desktop only) */}
            {!isMobile && (
              <div className="lg:col-span-1">
                <FiltersSidebar
                  filters={filters}
                  onJobTypeToggle={handleJobTypeToggle}
                  onPositionToggle={handlePositionToggle}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  positionOptions={POSITION_OPTIONS}
                />
              </div>
            )}

            {/* Right Section - Open Positions */}
            <div className={isMobile ? 'w-full' : 'lg:col-span-3'}>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-300">
                <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>Open Positions</h2>
                <span className={`text-gray-600 font-medium ${isMobile ? 'text-sm' : ''}`}>
                  {filteredPostings.length} {filteredPostings.length === 1 ? 'Result' : 'Results'}
                </span>
              </div>

              {loadingJobPostings ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : filteredPostings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No Job Postings Found
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Display Job Postings */}
                  {filteredPostings.map((posting) => (
                    <div
                      key={posting.id}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{posting.jobTitle}</h3>
                         
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Job Type:</span> {posting.jobType}
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-gray-700 leading-relaxed">
                          {posting.jobDescription ? extractJobSummary(posting.jobDescription, 30) : 'Job description not available.'}
                        </p>
                      </div>

                        <div className={`flex ${isMobile ? 'flex-col space-y-2 -mx-6 px-6' : 'items-center justify-between'}`}>
                          <div className={`flex ${isMobile ? 'flex-col w-full space-y-2' : 'space-x-3'}`}>
                            <button
                              onClick={(e) => handleSaveJob(posting.id, e)}
                              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                                isMobile ? 'w-full' : ''
                              } ${
                                savedJobs.has(posting.id)
                                  ? 'bg-blue-600 text-white text-sm'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm'
                              }`}
                            >
                              {savedJobs.has(posting.id) ? (
                                <>
                                  <FaBookmarkSolid className="w-4 h-4" />
                                  <span>Saved</span>
                                </>
                              ) : (
                                <>
                                  <FaBookmark className="w-4 h-4" />
                                  <span>Save</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleReadMore(posting)}
                              className={`bg-blue-600 text-sm text-white px-2 py-2 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap ${
                                isMobile ? 'w-full' : ''
                              }`}
                            >
                              Read Full Job Description
                            </button>
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Mobile Filters Bottom Sheet */}
      {isMobile && mobileFiltersOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setMobileFiltersOpen(false)}
          ></div>
          {/* Bottom Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-lg z-50 flex flex-col max-h-[80vh]">
            {/* Sticky Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">Filters</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 relative">
              {/* Clear Filters - Sticky Above Job Type, Right Corner */}
              <div className="sticky top-0 bg-white z-10 px-4 pt-4 pb-2 flex justify-end">
                <button
                  onClick={() => {
                    handleClearFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors relative group"
                >
                  {/* <FaUndo className="w-4 h-4" /> */}
                  <span className="text-xs">Clear filters</span>
                  <FaUndo className="w-2 h-2" />
                  <span className="absolute bottom-0 left-0 right-0 h-[1px] bg-gray-800 scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                </button>
              </div>
              <div className="p-4 pt-2">
                {/* Job Type Filter */}
                <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Job Type</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.jobTypes.includes(JobType.FULL_TIME)}
                      onChange={() => handleJobTypeToggle(JobType.FULL_TIME)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Full-Time</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.jobTypes.includes(JobType.PART_TIME)}
                      onChange={() => handleJobTypeToggle(JobType.PART_TIME)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Part-Time</span>
                  </label>
                </div>
              </div>

              {/* Position Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Position</h3>
                <div className="space-y-2">
                  {POSITION_OPTIONS.map((position) => (
                    <label key={position} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.positions.includes(position)}
                        onChange={() => handlePositionToggle(position)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{position}</span>
                    </label>
                  ))}
                </div>
              </div>

              </div>
              {/* Apply Button - Sticky at Bottom */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 mt-4">
                <button
                  onClick={() => {
                    handleApplyFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 transition-colors font-semibold"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Floating Filter Button - Mobile */}
      <FloatingFilterButton
        onClick={() => setMobileFiltersOpen(true)}
        show={isMobile}
      />
    </div>
  );
};

export default Careers;

